import {
  ProcessUrlResponse,
  ProcessUrlResponseType,
  processUrlResponse,
} from '../types';
import { ObservableMap } from '../lib/map/observableMap';
import { processUrl } from '../actions/accountActions';
import { VERDICT_TIMEOUT_MS } from './verdictTimeout';
import {
  ReportSiteAction,
  ReportSiteReason,
  WebEvent,
} from '../types/api/events';
import { now, nowImmediate } from '../lib/dateTime';
import { getDeviceAndUserStore } from '../storage/stores';
import {
  getNextAvailableHour,
  isScheduleRestricted,
} from '../validators/timeScheduleValidator';
import {
  DailyQuotaRuleStateType,
  dailyQuotaRuleChecker,
} from '../checkers/dailyQuotaRuleChecker';
import { TimeDuration } from '../lib/timeDuration';
import {
  pauseInternetRuleChecker,
  PauseInternetRuleState,
} from '../checkers/pauseInternetRuleChecker';
import {
  routineRuleChecker,
  RoutineContentFilteringDecisionType,
  RoutineRuleStateType,
} from '../checkers/routineRuleChecker';

export type ProcessedVerdict = {
  verdict: ProcessUrlResponse;
  webEvent?: WebEvent;
  requestId?: string;
};

export type BlockResult = {
  verdict: ProcessUrlResponse;
};

// List of processed verdicts
const verdictCache = new ObservableMap<string, ProcessedVerdict>();

/**
 * Returns the verdict for a navigation.
 *
 * Verdicts are pre-processed when receiving an HTTP(s) event on {@link processNetworkRequest},
 * taking into consideration the LW verdict if applies.
 *
 * If the verdict is already available, it will return it.
 * If the verdict is still being pre-processed, will return it as soon as it's received, with a maximum wait of 5 seconds.
 * If the verdict is not being pre-processed, or didn't finish pre-processing it in time, it will process it and return it right away.
 */
export const getVerdict = async (
  url: string,
  skipCache = false
): Promise<ProcessedVerdict> => {
  const cachedVerdict = skipCache ? undefined : verdictCache.get(url);

  // Verdict is not being-pre processed or available
  if (!cachedVerdict) {
    return await processVerdict(url);
  }

  // Verdict it's already available
  if (isVerdictAvailable(cachedVerdict.verdict)) {
    if (needsWebRulesVerdict(cachedVerdict)) {
      return await processVerdict(url);
    }
    return applyContentFilteringRoutine(cachedVerdict);
  }

  // Return verdict when available, if not undefined. Or process it if not.
  const newVerdict = await verdictCache.waitForValue(
    url,
    value => isVerdictAvailable(value?.verdict),
    VERDICT_TIMEOUT_MS
  );
  if (!newVerdict || needsWebRulesVerdict(newVerdict)) {
    return await processVerdict(url);
  }
  return applyContentFilteringRoutine(newVerdict);
};

const isVerdictAvailable = (verdict?: ProcessUrlResponse) =>
  verdict !== undefined && verdict.type !== ProcessUrlResponseType.PENDING;

const needsWebRulesVerdict = (verdict: ProcessedVerdict): boolean =>
  verdict.webEvent === undefined &&
  verdict.verdict.type === ProcessUrlResponseType.ALLOW;

const processVerdict = async (url: string) => {
  // Rule-driven blocks (pause, screen-free-time, quota, schedule) outrank
  // everything below in the User Rules Priorities workflow: PAR
  // exceptions, autoblock, default rules and the content filtering routine.
  // Non-counting routines intentionally let the request reach web-rule
  // processing so the routine can decide with the URL categories.
  // Only logs coming from here should be printed to avoid spam
  const blockResult = await shouldBlock({ shouldLog: true });
  if (blockResult) {
    const verdict: ProcessedVerdict = { verdict: blockResult.verdict };
    saveVerdict(url, verdict);
    return verdict;
  }

  const baseVerdict = await processUrl(url);
  const verdict = applyContentFilteringRoutine(baseVerdict);
  saveVerdict(url, verdict);
  return verdict;
};

const applyContentFilteringRoutine = (
  baseVerdict: ProcessedVerdict
): ProcessedVerdict => {
  if (!isDefaultWebRulesVerdict(baseVerdict)) {
    return baseVerdict;
  }

  const routineDecision = routineRuleChecker.checkContentFiltering(
    baseVerdict.webEvent.categories
  );
  if (!routineDecision) {
    return baseVerdict;
  }

  if (routineDecision.type === RoutineContentFilteringDecisionType.ALLOW) {
    return {
      ...baseVerdict,
      verdict: processUrlResponse.allow(),
      webEvent: {
        ...baseVerdict.webEvent,
        action: ReportSiteAction.ALLOW,
        reason: ReportSiteReason.NO_MATCH,
        categories_match: [],
      },
    };
  }

  return {
    ...baseVerdict,
    verdict: processUrlResponse.blockByContentFilteringRoutine({
      categories: routineDecision.categoriesMatch,
    }),
    webEvent: {
      ...baseVerdict.webEvent,
      action: ReportSiteAction.BLOCK,
      reason: ReportSiteReason.CATEGORY_BLOCK,
      categories_match: [...routineDecision.categoriesMatch],
    },
  };
};

const isDefaultWebRulesVerdict = (
  processedVerdict: ProcessedVerdict
): processedVerdict is ProcessedVerdict & { webEvent: WebEvent } =>
  processedVerdict.webEvent?.reason === ReportSiteReason.NO_MATCH ||
  processedVerdict.webEvent?.reason === ReportSiteReason.CATEGORY_BLOCK;

export const getVerdictByRequestId = (
  requestId: string
): Promise<ProcessedVerdict | undefined> => {
  for (const [key, value] of verdictCache.entries()) {
    if (value.requestId === requestId) {
      return getVerdict(key);
    }
  }
  return Promise.resolve(undefined);
};

export const saveVerdict = (url: string, verdict: ProcessedVerdict) =>
  verdictCache.set(url, verdict);

export const setPendingVerdict = (url: string) =>
  verdictCache.set(url, { verdict: processUrlResponse.pending() });

/**
 * Rule refreshes must invalidate verdicts because cached blocking states can
 * otherwise survive extra-time grants, pause endings, or routine changes.
 */
export const clearVerdictCache = () => verdictCache.clear();

const logShouldBlock = (shouldLog: boolean, message: string): void => {
  if (shouldLog) {
    console.log(message);
  }
};

const checkPauseInternetBlock = (
  shouldLog: boolean
): BlockResult | undefined => {
  const pauseState = pauseInternetRuleChecker.check();
  if (pauseState.state !== PauseInternetRuleState.PAUSED_FOR) {
    return undefined;
  }
  logShouldBlock(
    shouldLog,
    '[shouldBlock] BLOCK_BY_PAUSE - Pause Internet is active'
  );
  return {
    verdict: processUrlResponse.blockByPause(
      getHoursAndMinutesLeft(pauseState.endsAtInSeconds)
    ),
  };
};

const checkScreenFreeTimeRoutineBlock = (
  shouldLog: boolean
): BlockResult | undefined => {
  const screenFreeTimeState = routineRuleChecker.checkScreenFreeTime();
  if (screenFreeTimeState.state !== RoutineRuleStateType.ROUTINE_ACTIVE) {
    return undefined;
  }
  logShouldBlock(
    shouldLog,
    '[shouldBlock] BLOCK_BY_SCREEN_FREE_TIME_ROUTINE - Routine is active'
  );
  return {
    verdict: processUrlResponse.blockByScreenFreeTimeRoutine(
      getHoursAndMinutesLeft(screenFreeTimeState.activeUntilInSeconds)
    ),
  };
};

const checkDailyQuotaBlock = async (
  shouldLog: boolean
): Promise<BlockResult | undefined> => {
  if (!routineRuleChecker.countsTowardsDailyTimeQuota()) {
    return undefined;
  }

  const currentDate = await now();
  const dailyQuotaState = await dailyQuotaRuleChecker.check(currentDate);
  if (dailyQuotaState.state !== DailyQuotaRuleStateType.QUOTA_EXCEEDED) {
    return undefined;
  }
  logShouldBlock(
    shouldLog,
    '[shouldBlock] BLOCK_BY_QUOTA - Device is over daily quota'
  );
  return { verdict: processUrlResponse.blockByQuota() };
};

const checkScheduleBlock = async (
  shouldLog: boolean
): Promise<BlockResult | undefined> => {
  const currentDate = await now();
  if (!isScheduleRestricted(currentDate, { shouldLog })) {
    return undefined;
  }
  logShouldBlock(
    shouldLog,
    '[shouldBlock] BLOCK_BY_SCHEDULE - Device is over schedule'
  );
  const nextAvailableHour = getNextAvailableHour(currentDate);
  return {
    verdict:
      nextAvailableHour === undefined
        ? processUrlResponse.blockBySchedule()
        : processUrlResponse.blockBySchedule({ nextAvailableHour }),
  };
};

/**
 * Evaluates rule-driven blocks for the current device and returns the
 * highest-precedence block result, or `undefined` when no rule blocks the
 * navigation.
 *
 * Precedence (highest first), matching the User Rules Priorities workflow:
 * pause internet, screen-free-time routine, daily quota when the active
 * routine counts towards it, restricted schedule. Future rule-based blocks
 * belong here so every caller stays in sync.
 */
export const shouldBlock = async ({
  shouldLog = false,
}: { shouldLog?: boolean } = {}): Promise<BlockResult | undefined> => {
  const deviceUser = getDeviceAndUserStore().get();
  if (!deviceUser) {
    logShouldBlock(shouldLog, '[shouldBlock] UNDEFINED - No device user');
    return undefined;
  }

  return (
    checkPauseInternetBlock(shouldLog) ??
    checkScreenFreeTimeRoutineBlock(shouldLog) ??
    (await checkDailyQuotaBlock(shouldLog)) ??
    (await checkScheduleBlock(shouldLog))
  );
};

const getHoursAndMinutesLeft = (
  endsAtInSeconds: number
): { hours: number; minutes: number } => {
  const nowSeconds = Math.floor(nowImmediate() / 1000);
  const remainingSeconds = endsAtInSeconds - nowSeconds;
  return TimeDuration.mapToHoursAndMinutesComponents(
    TimeDuration.fromSeconds(remainingSeconds)
  );
};
